/**
 * Validation for technical review, final approval and post-approval
 * corrections — modules C7 and C8, Phase 9.
 *
 * READ CLAUDE.md SECTIONS 4, 5 AND 8 FIRST. A reviewer and an approver record
 * A DECISION, THE REVISION THEY ACTED ON, AND THEIR WORDS. There is no schema
 * here for a conformity statement, a pass, a fail, a limit or a verdict on the
 * material — for any test — because that is test science and it is gated on a
 * validated worked example.
 *
 * EVERY SCHEMA IS STRICT. An unknown key is refused with a 400 rather than
 * silently dropped, so a client that sends `performedBy`, `actedBy` or
 * `conformity` learns immediately that no such thing can be written here.
 *
 * WHY `revisionNumber` IS REQUIRED ON EVERY ACT: a reviewer accepts a specific
 * submission, not "whatever is current". If the result has been returned and
 * resubmitted since the reviewer opened it, the number no longer matches and
 * the server refuses with a conflict rather than recording an acceptance of
 * text nobody read.
 */
import { z } from 'zod';
import { boundedText, listQuerySchema, reasonSchema, uuidSchema, } from './primitives.js';
/** An optional note: absent, or actual content. Never blank. */
const optionalNote = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value));
/**
 * The revision the person acted on, as they saw it. Positive, because
 * revisions are counted from the first submission.
 */
const revisionNumberSchema = z.coerce.number().int().positive();
// ---------------------------------------------------------------------------
// Technical review (C7)
// ---------------------------------------------------------------------------
export const reviewQueueQuerySchema = listQuerySchema.extend({
    catalogueItemId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
});
/**
 * Technical acceptance.
 *
 * The note is OPTIONAL: the record already states who accepted which revision
 * and when, and requiring prose for a routine acceptance produces filler.
 * Accepting is NOT approval — it moves the result to a separate stage held
 * behind a separate permission.
 */
export const reviewAcceptSchema = z
    .object({
    revisionNumber: revisionNumberSchema,
    note: optionalNote(4000),
})
    .strict();
/** Sending the record back to its performer. The reason is mandatory and is
 *  shown to them, verbatim, and kept for good. */
export const reviewReturnSchema = z
    .object({
    revisionNumber: revisionNumberSchema,
    reason: reasonSchema,
})
    .strict();
/** Requiring the WORK to be performed again — a different act from returning
 *  the record for correction. The reason is mandatory. */
export const reviewRequireRetestSchema = z
    .object({
    revisionNumber: revisionNumberSchema,
    reason: reasonSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Final approval (C8)
// ---------------------------------------------------------------------------
export const approvalQueueQuerySchema = listQuerySchema.extend({
    catalogueItemId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
});
/**
 * Approve a technically accepted result.
 *
 * THE APPROVAL SAYS THE RECORD WAS CHECKED. It does not say the material
 * passed, and there is deliberately no field here in which to claim that.
 */
export const approveResultSchema = z
    .object({
    revisionNumber: revisionNumberSchema,
    note: optionalNote(4000),
})
    .strict();
export const approvalReturnSchema = z
    .object({
    revisionNumber: revisionNumberSchema,
    reason: reasonSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Post-approval correction
// ---------------------------------------------------------------------------
/**
 * Open a correction on an approved result.
 *
 * This is a PROCEDURE, not an edit: it releases the lock, sends the result
 * back to its performer and requires review and approval again. The reason is
 * mandatory, because a laboratory must be able to say later why an approved
 * result was reopened.
 */
export const openCorrectionSchema = z
    .object({
    reason: reasonSchema,
    /** What the laboratory intends to put right, in its own words. */
    summary: boundedText(3, 4000).optional(),
})
    .strict();
export const correctionListQuerySchema = listQuerySchema.extend({
    status: z.enum(['open', 'resolved']).optional(),
});
/** The technician's own returned work. Nothing to filter but the page. */
export const returnedWorkQuerySchema = listQuerySchema;
//# sourceMappingURL=review.js.map